import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { useUser } from '../../UserContext'
import { useAuth } from '../Routes/AuthProvider'
import LoadingProgress from '../Elements/LoadingProgress'
import { useAppSnackbar } from '../../hooks/useAppSnackbar'
import { useTranslation } from 'react-i18next'

const parseActorKeyFromUrlParam = (
  actorKeyParam?: string
): number | undefined => {
  if (!actorKeyParam) return undefined

  const parsedActorKey = Number(actorKeyParam)
  return Number.isFinite(parsedActorKey) && parsedActorKey > 0
    ? parsedActorKey
    : undefined
}

const DashboardActorRedirect: React.FC = () => {
  const navigate = useNavigate()
  const { actorKey: actorKeyParam } = useParams<{
    actorKey: string
  }>()

  const { isLoggedIn, userDetails, updateActingAs } = useAuth()
  const { uniqueActiveUserRoles, selectedActorRole } = useUser()
  const { handleMessage } = useAppSnackbar()
  const { t } = useTranslation()
  const [isProcessingRedirect, setIsProcessingRedirect] = useState(true)

  const requestedActorKey = useMemo(
    () => parseActorKeyFromUrlParam(actorKeyParam),
    [actorKeyParam]
  )

  const hasActiveUserRoles = uniqueActiveUserRoles.length > 0

  const doesRequestedActorBelongToUser = useMemo(() => {
    if (requestedActorKey === undefined) return false

    return uniqueActiveUserRoles.some((userRole) => {
      return userRole.actorKey === requestedActorKey && userRole.validNow
    })
  }, [requestedActorKey, uniqueActiveUserRoles])

  useEffect(() => {
    let isMounted = true

    const goToDashboard = () =>
      navigate('/account/dashboard', { replace: true })

    const goToLogin = () =>
      navigate('/login', { replace: true, state: { from: location.pathname } })

    const performActorRedirect = async () => {
      if (!isMounted) return
      setIsProcessingRedirect(true)

      try {
        if (!isLoggedIn) {
          return goToLogin()
        }

        /**
         * At this point, the user must be logged in
         * but given the async nature of auth and user details loading,
         * we need to check if the selectedActorRole is available
         * before making decisions based on it and avoid potential React async operations issues
         */
        if (selectedActorRole) {
          const lastSelectedActorKey = userDetails.actingAs

          const shouldGoToLoggedInActorDashboard =
            requestedActorKey === undefined ||
            (typeof lastSelectedActorKey === 'number' &&
              lastSelectedActorKey === requestedActorKey) ||
            (hasActiveUserRoles && !doesRequestedActorBelongToUser)

          if (shouldGoToLoggedInActorDashboard) {
            !doesRequestedActorBelongToUser &&
              handleMessage(
                t(
                  'DashboardActorRedirect.RequestedActorNotBelongToUser',
                  `Unable to view other user's dashboard.`
                ),
                'warning'
              )
            return goToDashboard()
          }

          await updateActingAs(requestedActorKey)
          return goToDashboard()
        }
      } catch {
        return goToDashboard()
      } finally {
        setIsProcessingRedirect(false)
      }
    }

    performActorRedirect()

    return () => {
      isMounted = false
    }
  }, [
    isLoggedIn,
    userDetails.actingAs,
    updateActingAs,
    requestedActorKey,
    hasActiveUserRoles,
    doesRequestedActorBelongToUser,
    navigate,
    selectedActorRole,
    handleMessage,
    t,
  ])

  if (isProcessingRedirect) {
    return <LoadingProgress />
  }

  return null
}

export default DashboardActorRedirect
